import { AfterViewInit, Component, inject, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, take, timeout } from 'rxjs';
import { StorageService } from '@core/services';
import { AuthService } from '@customer/services';
import { ContactService } from '@customer/contact';
import { LoggedInUser } from '@legacy-models/portal/Account';

interface SessionState {
  step: 'initializing' | 'complete' | 'error';
  user: LoggedInUser | null;
  error: string | null;
}

@Component({
  selector: 'oke-session',
  imports: [],
  templateUrl: './session.html',
  styleUrl: './session.scss'
})
export class Session implements AfterViewInit, OnDestroy {
  bodyElem = document.body;
  private readonly _authSvc = inject(AuthService);
  private readonly _contactSvc = inject(ContactService);
  private readonly _storageSvc = inject(StorageService);
  private readonly _router = inject(Router);
  private readonly _$sessionState = signal<SessionState>({
    step: 'initializing',
    user: null,
    error: null
  });
  readonly $sessionState = this._$sessionState.asReadonly();
  private _secureAuthSubscription?: Subscription;

  constructor() {
    this.initializeExternalView();
    this._initializeSession();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._storageSvc.setCurrentPage();
    });
  }

  ngOnDestroy(): void {
    if (this._secureAuthSubscription) {
      this._secureAuthSubscription.unsubscribe();
    }
  }

  /**
   * session intialization from user returned from SecureAuth
   */
  private _initializeSession(): void {
    this._$sessionState.update(s => ({
      ...s,
      step: 'initializing',
      error: null
    }));

    this._secureAuthSubscription = this._authSvc.secureAuthUser$.pipe(take(1), timeout(10000)).subscribe({
      next: user => {
        if (user) {
          this._establishUserSession(user);
        } else {
          this._handleAuthError('No authentication data available. Please login again.');
        }
      },
      error: error => {
        console.error('Secure auth subscription error: Missing authentication data', error);
        this._handleAuthError('Authentication service timeout. Please try logging in again.');
      }
    });
  }

  /**
   * @description Establishes user session with backend services
   * @param user Authenticated user object
   */
  private _establishUserSession(user: { profile?: { email?: string; sub?: string }; id_token?: string }): void {
    try {
      const email = user.profile?.email;
      const userName = user.profile?.sub;
      const authToken = user.id_token;

      if (!email || !userName || !authToken) {
        this._handleAuthError('Invalid user data received. Please login again.');
        return;
      }

      const loggedInUser: LoggedInUser = {
        Email: email,
        UserName: userName
      };
      sessionStorage.setItem('authToken', authToken);

      this._contactSvc.start(loggedInUser).subscribe({
        next: response => {
          void response;
          this._$sessionState.set({
            step: 'complete',
            user: loggedInUser,
            error: null
          });
          this._storageSvc.setloggedInUser(loggedInUser);
        },
        error: error => {
          console.error('Backend session establishment failed:', error);
          this._handleAuthError('Failed to establish session with backend services. Please try again.');
        }
      });
    } catch (error) {
      console.error('User session setup failed:', error);
      this._handleAuthError('Invalid user data received. Please login again.');
    }
  }

  /**
   *
   * @returns session state
   */
  getStepMessage(): string {
    const step = this.$sessionState().step;
    switch (step) {
      case 'initializing':
        return 'Initializing your session...';
      case 'complete':
        return 'Almost ready!';
      case 'error':
        return 'Session setup failed';
      default:
        return 'Initializing your session...';
    }
  }

  backToLogin(): void {
    this._authSvc.logout();
    this._router.navigate(['/login']);
  }

  //Remove internal styling from body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeExternalView(): void {
    this.bodyElem.className = 'is-unauthenticated';
  }

  /**
   * @description Handles authentication errors and sets error state
   * @param message Error message to display
   */
  private _handleAuthError(message: string): void {
    this._$sessionState.update(s => ({
      ...s,
      step: 'error',
      error: message
    }));
  }
}
